import { useGetFeatureFlags } from "@/api/config/config"
import type { GetFeatureFlagsResponse } from "@/api/model"
import {
  defineAsyncComponent,
  defineComponent,
  h,
  type AsyncComponentLoader,
  type Component,
} from "vue"

type FeatureFlagKey = keyof GetFeatureFlagsResponse

/**
 * Picks between two route components based on a feature flag. Renders nothing
 * while the flags query is in flight (typically cached by app boot).
 *
 * @example
 *   component: featureFlaggedView("soRewardingV2", {
 *     off: () => import("@/views/myaccount/loyalty/RewardsView.vue"),
 *     on:  () => import("@/views/myaccount/loyalty/SoRewardingView.vue"),
 *   })
 */
export const featureFlaggedView = (
  flag: FeatureFlagKey,
  variants: {
    off: AsyncComponentLoader<Component>
    on: AsyncComponentLoader<Component>
  },
) => {
  const Off = defineAsyncComponent(variants.off)
  const On = defineAsyncComponent(variants.on)

  return defineComponent({
    name: `FeatureFlagged_${flag}`,
    setup() {
      const { data: flags, isLoading } = useGetFeatureFlags()
      return () => {
        if (isLoading.value) return null
        const Variant = flags.value?.[flag] ? On : Off
        return h(Variant)
      }
    },
  })
}
